
import { mapGetters } from 'vuex';
import gql from 'graphql-tag';
import Logging from '@/logging';
import ChannelEndpointDialog from '@/components/_dialogs/ChannelEndpointDialog.vue';
import axios from 'axios';
import { Address } from '@/schema/address';
import { DEACTIVATE_ENDPOINT } from '@/gql';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';

export default {
	components: {
		ChannelEndpointDialog
	},
	props: {
		recipient: {
			type: String,
			required: false,
			default: null
		},
		legalEntity: {
			type: String,
			required: true
		},
		endpointSelectable: {
			type: Boolean,
			required: false,
			default: () => false
		},
		onSelectEndpoint: {
			type: Function,
			required: false,
			default: null
		}
	},
	data() {
		return {
			endpoints: undefined,
			recipientData: undefined,
			channelEndpointDialogVisible: false,
			channelEndpointDialogEndpoint: null,
			handleRemoveChannelEndpointLoading: false
		};
	},
	computed: {
		...mapGetters(['env', 'selectedParticipantStore', 'loggedInUser']),
		allowEdit() {
			const licences = this.$store.state.risolto.licences[this.selectedParticipantStore];
			const permissions = this.$store.state.risolto.permissions[this.selectedParticipantStore];
			return hasLicenseAndPermission(
				licences,
				permissions,
				'CORE',
				'UI_EDIT_CONTACTS'
			);
		},
		activatedEndpoints: function() {
			try {
				return this.endpoints.filter(e => !e.deactivated_at).filter(e => e.origin !== 'RISOLTO');
			} catch {
				return [];
			}
		},
		risoltoEndpoints: function () {
			try {
				return this.endpoints.filter(e => e.origin === 'RISOLTO');
			} catch {
				return [];
			}
		}
	},
	watch: {
		'$store.state.apollo.risolto_contact': function() {
			this.getContacts();
		},
		'$store.state.apollo.risolto_channel_endpoint': function() {
			this.getContacts();
		},
		recipient() {
			this.getContacts();
		}
	},
	created() {
		this.getContacts();
	},
	methods: {
		handleSelectEndpoint(channelEndpoint) {
			if (typeof this.onSelectEndpoint === 'function') {
				this.onSelectEndpoint(channelEndpoint);
			}
		},
		getContacts() {
			if (this.recipient === null || this.recipient === undefined) {
				Logging.log('Recipient not yet set...');
				return {};
			}

			const vars = {
				participant: this.selectedParticipantStore,
				recipient: this.recipient
			};

			this.$apollo
				.query({
					query: this.makeQuery(),
					variables: vars
				})
				.then(result => {
					if (result.data) {
						this.endpoints = result.data.recipient[0].main_endpoints;
						this.recipientData = result.data.recipient[0];
						Logging.log('MainContacts.getContacts - data: ', result.data.recipient[0]);
						Logging.log('MainContacts.getContacts - this.endpoints: ', this.endpoints);
					}
				})
				.catch(e => {
					Logging.error('Error get endpoints: ', e);
				});
		},
		address(endpoint) {
			if (endpoint.channel !== 'MAIL') {
				return undefined;
			}
			var addressData;
			if (endpoint.endpoint_details.address) {
				addressData = endpoint.endpoint_details.address;
			} else {
				addressData = endpoint.endpoint_details;
			}

			const address: Address = {
				company_name: addressData.company_name ? addressData.company_name : '',
				street_name: addressData.street_name,
				street_number: addressData.street_number,
				post_box: addressData.post_box,
				street_name_number_box: addressData.street_name_number_box,
				zip_code: addressData.zip_code ? addressData.zip_code : '',
				city: addressData.city ? addressData.city : '',
				iso_country_code: addressData.iso_country_code ? addressData.iso_country_code : ''
			};
			Logging.log('adress:', address);
			return address;
		},
		makeQuery: function() {
			let query = gql`
			query details(
				$participant: uuid!
				$recipient: uuid!
			){
				recipient: risolto_recipient(
					where:{
						reference: { _eq: $recipient }
						participant: { _eq: $participant }
					}
				) {
					legal_entity
					language_code
					preferred_invoicing_channel
					main_endpoints:channel_endpoints(
						where:{
							contact:{_is_null:true}
							channel: { _neq: "SMS" }
							deactivated_at: { _is_null: true }
						},
						order_by: [{channel: asc}]
					){
						reference
						channel
						endpoint
						endpoint_details
						deactivated_at
						origin
						locale
					}
				}
			}
			`;
			return query;
		},
		handleDeactivate(reference) {
			this.$apollo
				.mutate({
					mutation: DEACTIVATE_ENDPOINT,
					variables: {
						participant: this.selectedParticipantStore,
						deactivated_by: this.loggedInUser.reference,
						reference: reference
					}
				})
				.then(result => {
					Logging.log('DEACTIVATE_ENDPOINT: ', result);
					this.$store.commit('apollo/RISOLTO_CONTACT');
					this.$notify({
						title: this.$t('success'),
						message: this.$t('endpointDeactivated'),
						type: 'success'
					});
				})
				.catch(e => {
					Logging.error('DEACTIVATE_ENDPOINT: ', e);
				});
		},
		handleAddChannelEndpoint() {
			this.channelEndpointDialogVisible = true;
			this.channelEndpointDialogEndpoint = null;
		},
		handleEditChannelEndpoint(endpoint) {
			this.channelEndpointDialogVisible = true;
			this.channelEndpointDialogEndpoint = endpoint;
		},
		async handleRemoveChannelEndpoint(endpoint) {
			if (this.handleRemoveChannelEndpointLoading) {
				return;
			}
			this.handleRemoveChannelEndpointLoading = true;

			const payload = {
				participant: this.selectedParticipantStore,
				legal_entity: this.legalEntity,
				endpoint: endpoint
			};

			try {
				await axios.delete(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/channel-endpoint`, {
					data: payload,
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				this.$store.commit('apollo/RISOLTO_CHANNEL_ENDPOINT');
			} catch (e) {
				//
			} finally {
				this.handleRemoveChannelEndpointLoading = false;
			}
		}
	}
};
